// src/services/demandes-client.service.ts
//
// Mai 2026 — MM-28/29/30
// Wrapper REST pour le module backend "Demandes client".

import axios from 'axios';
import { redirectToLogin } from '../utils/authRedirect';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:1920/api';

export interface DemandeClientItem {
  typeOperationId: string;
  typeOperationCode: string;
  typeOperationLabel: string;
  categorie: string;
  description: string;
  sens: string;
  workflowId: string;
  workflowCode: string;
  workflowNom: string;
  entiteId: string;
  entiteCode: string | null;
  entiteNom: string | null;
  domaine: string;
  etapeSaisieId: string;
  etapeSaisieNom: string;
  etapeSaisieOrdre: number;
  delaiSaisieHeures: number;
  formTemplateCodes: string[];
  formConfigChampsCount: number;
  nbEtapesTotal: number;
  // ── Pour le menu latéral, qui lit cette même liste ─────────────────────
  /** Modules dont le menu propose ce processus. */
  moduleCodes: string[];
  /** Ordre d'affichage dans le menu. */
  ordre: number;
  /** Type système : lien dédié dans le menu, hors des listes dynamiques. */
  systeme: boolean;
  /** Faux pour un processus interne (sans étape de saisie en ligne). */
  saisieEnLigne: boolean;
}

// ── Cache de la liste des processus ────────────────────────────────────────
// SOURCE UNIQUE (décision EEC, 17/09/2026) : le menu latéral et le catalogue
// des processus lisent la même liste serveur. Le menu est monté sur chaque
// page : la liste y est chargée une fois, puis servie depuis ce cache jusqu'à
// son invalidation (rafraîchissement des processus, modification d'un type).
let promesseCatalogue: Promise<DemandeClientItem[]> | null = null;

/** Oublie la liste en cache : le prochain affichage la recharge. */
export function invaliderCatalogueProcessus(): void {
  promesseCatalogue = null;
}

function authHeaders() {
  const token =
    localStorage.getItem('token') ||
    localStorage.getItem('access_token') ||
    localStorage.getItem('auth_token');
  return { Authorization: `Bearer ${token}` };
}

function handleErr(e: any): never {
  if (e?.response?.status === 401) {
    localStorage.removeItem('token');
    localStorage.removeItem('access_token');
    redirectToLogin();
  }
  throw e;
}

export const demandesClientService = {
  /**
   * Liste les demandes client pour le back-office (auth requise).
   * Scope par défaut sur l'entité du JWT, sauf super_admin.
   */
  async catalogue(opts?: {
    entiteId?: string;
    domaine?: string;
    search?: string;
  }): Promise<DemandeClientItem[]> {
    try {
      const params: any = {};
      if (opts?.entiteId) params.entiteId = opts.entiteId;
      if (opts?.domaine) params.domaine = opts.domaine;
      if (opts?.search) params.search = opts.search;
      const r = await axios.get(`${API_URL}/demandes-client/catalogue`, {
        headers: authHeaders(),
        params,
      });
      return Array.isArray(r.data) ? r.data : [];
    } catch (e) {
      return handleErr(e);
    }
  },

  /**
   * Liste des processus du back-office, depuis le cache partagé (menu latéral).
   * Un échec n'est pas mis en cache : la prochaine lecture réessaie.
   */
  async catalogueEnCache(): Promise<DemandeClientItem[]> {
    if (!promesseCatalogue) {
      promesseCatalogue = demandesClientService.catalogue({}).catch(err => {
        promesseCatalogue = null;
        throw err;
      });
    }
    return promesseCatalogue;
  },

  /**
   * Catalogue public (sans auth) pour le portail abonné.
   * @param slug code/slug d'entité (ex: 'EEC' ou 'eec')
   */
  async cataloguePublic(slug: string, opts?: {
    domaine?: string;
    search?: string;
  }): Promise<{ entite: { id: string; code: string; slug: string; nom: string }; items: DemandeClientItem[] }> {
    const params: any = {};
    if (opts?.domaine) params.domaine = opts.domaine;
    if (opts?.search) params.search = opts.search;
    const r = await axios.get(`${API_URL}/demandes-client/portail/${encodeURIComponent(slug)}`, {
      params,
    });
    return r.data;
  },
};
